import React, { FC, memo, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isEmpty, isNil } from 'lodash';
import { useSelector } from 'react-redux';
// Import Hooks
import { usePartnerDateFormats, useRequest } from 'hooks';
// Import Services
import { ABTestingHttpService, CampaignHttpService } from 'services/http';
// Import UI Components
import { Alert, BusyLoader, Empty, Tab, Tabs } from '@geneui/components';
// Import Components
import ABTestGeneralDetails from './ABTestGeneralDetails';
import ABTestChartGroup from './ABTestChartGroup';
// Import Types
import { PromotionPresetsAPIModel, TestResultsAPIModel } from './types';
// Import Constants
import { l } from 'constants/common';
import { parseToUIModel } from './config';
import { ABTestCampaignData } from 'types';

interface Props {
    campaignId: string;
    campaignData: {
        ABTest: ABTestCampaignData;
        NextRunDate: string | null;

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        [key: string]: any;
    };
}

interface State {
    header: {
        settings: {
            Settings: {
                CurrencyId: string;
            };
        };
    };
    currencyRates: object;
}

const CampaignReportABTest: FC<Props> = ({ campaignId, campaignData }) => {
    const { t } = useTranslation();
    const { partnerDateFormat } = usePartnerDateFormats();

    const currencyId = useSelector((state: State) => state.header?.settings?.Settings?.CurrencyId);
    const rate = useSelector((state: State) => state.currencyRates?.[currencyId]?.EUR)?.Rate;

    const { doGetRequest } = useRequest();

    const [testResultsAPIModel, setTestResultsAPIModel] = useState<TestResultsAPIModel[] | null>(null);
    const [promotionPresetsAPIModel, setPromotionPresetsAPIModel] = useState<PromotionPresetsAPIModel[] | null>(null);

    const isLoading: boolean = isNil(testResultsAPIModel) || isNil(promotionPresetsAPIModel);

    const { getCampaignABTestResult, getPresets } = useMemo(
        () => ({
            getCampaignABTestResult: CampaignHttpService.getCampaignABTestResult(),
            getPresets: ABTestingHttpService.getPresets(),
        }),
        [],
    );

    const aBTestResultsUIModel = useMemo(() => {
        if (!isNil(testResultsAPIModel) && !isNil(promotionPresetsAPIModel)) {
            return parseToUIModel({
                testResultsAPI: testResultsAPIModel,
                promotionPresets: promotionPresetsAPIModel,
                t,
                partnerDateFormat,
                campaignNextRunDate: campaignData.NextRunDate,
                currencyId,
                rate,
            });
        }
    }, [
        testResultsAPIModel,
        promotionPresetsAPIModel,
        t,
        campaignData.NextRunDate,
        currencyId,
        rate,
        partnerDateFormat,
    ]);

    const init = () => {
        doGetRequest(getCampaignABTestResult.request, {
            queryString: { CampaignId: campaignId },
            successCallback: (data: TestResultsAPIModel[]) => setTestResultsAPIModel(data),
        });

        doGetRequest(getPresets.request, {
            successCallback: (data: PromotionPresetsAPIModel[]) => setPromotionPresetsAPIModel(data),
        });
    };

    const cleanUp = () => {
        return () => {
            getCampaignABTestResult.cancel('CampaignReportABTest:getCampaignABTestResult');
            getPresets.cancel('CampaignReportABTest:getPresets');
        };
    };

    useEffect(init, []);
    useEffect(cleanUp, []);

    return (
        <div className="campaign-report-ab-test-tab-container">
            <BusyLoader isBusy={isLoading || isNil(aBTestResultsUIModel)} type="spinner" spinnerSize="medium">
                {isEmpty(aBTestResultsUIModel) ? (
                    <Empty title={t(l.NoDataToDisplay)} />
                ) : (
                    <Tabs type="box" position="top">
                        {aBTestResultsUIModel?.map(
                            ({ tabTitle, generalDetails, kpiList, isResultEmpty }, index: number) => {
                                return (
                                    <Tab key={index} title={tabTitle}>
                                        <div className="campaign-report-ab-test-scrollable-container">
                                            <ABTestGeneralDetails generalDetails={generalDetails} />
                                            {isResultEmpty && (
                                                <Alert
                                                    closable={false}
                                                    title={t(l.ABTestNoResultYet)}
                                                    type="warning"
                                                    iconProps={{ type: 'bc-icon-info' }}
                                                />
                                            )}
                                            <ABTestChartGroup kpiList={kpiList} />
                                        </div>
                                    </Tab>
                                );
                            },
                        )}
                    </Tabs>
                )}
            </BusyLoader>
        </div>
    );
};

export default memo(CampaignReportABTest);
